/**
 * comboResource — the combo catalog, authored against the resource-kit v2
 * model/view contracts and consumed directly as a `ResourceBundle` by
 * `ResourcePage` (#1805 slice A2).
 *
 * The three-way split and the reasons for it are the same as its sibling
 * `../dishes/schema.tsx`, which spells them out: the model owns the record, the
 * list view owns the one ordered `items` sequence and therefore the visibility
 * modifiers, and the form view owns the groups plus the tab rail. Two points
 * are specific to combos:
 *
 *   - `price` stays `text`, not `currency`. The wire type is a decimal string
 *     with no currency column beside it, and `currency` would demand a
 *     `currencyField` naming one — the CSV lock pins the resulting export text.
 *   - The slot editor is the combo's second half; as the only tab, the rail
 *     opens on it and no navigation is added.
 *
 * `searchable` is deliberately absent, exactly as before — see the dish module
 * for why the absence is the declaration.
 */
import {
  createFieldConstructors,
  defineFormView,
  defineListView,
  defineModel,
  type EnumOption,
} from '@resource-kit/model';
import type { ResourceBundle } from '@resource-kit/controllers/react';
import type { Combo } from '@/services';
import { comboAPI, toListResult } from '@/services';
import { CATALOG_COMBO_SORTS } from '@/services/filters.generated';
import { PERMISSIONS } from '@/permissions';
import { SlotEditorCard } from './components/SlotEditorCard';

const statusOptions: EnumOption[] = [
  { value: 'active', label: 'combo.enum.status.active', color: 'success' },
  { value: 'archived', label: 'combo.enum.status.archived', color: 'secondary' },
];

const f = createFieldConstructors<Combo>();

export const comboModel = defineModel<Combo>()({
  name: 'combo',
  idField: 'id',
  recName: 'name',
  fields: {
    id: f.id({ label: 'combo.field.id' }),
    code: f.text({ label: 'combo.field.code' }),
    name: f.text({ label: 'combo.field.name' }),
    description: f.text({ label: 'combo.field.description' }),
    price: f.text({ label: 'combo.field.price' }),
    status: f.enum({ label: 'combo.field.status', options: statusOptions }),
    created_at: f.datetime({ label: 'combo.field.createdAt' }),
    updated_at: f.datetime({ label: 'combo.field.updatedAt' }),
  },
});

export const comboListView = defineListView(comboModel, {
  items: [
    { field: 'id', columnInvisible: true, invisible: true },
    { field: 'code' },
    { field: 'name' },
    { field: 'description', columnInvisible: true },
    { field: 'price' },
    { field: 'status' },
    { field: 'created_at', columnInvisible: true, invisible: true },
    { field: 'updated_at', columnInvisible: true, invisible: true },
  ],
  defaultSort: { field: 'created_at', dir: 'desc' },
});

// Every EDITABLE field lives in the first tab (#1922). Under the #1918 tab
// default each group is a rail trigger, so `price` alone in a `commercial`
// group meant saving a combo took two panels — and the second one held a
// single scalar. `meta` stays behind because both of its fields are
// `serverAssigned` and therefore read-only on every surface.
export const comboFormView = defineFormView(comboModel, {
  groupDisplay: 'tab',
  children: [
    {
      kind: 'fields',
      id: 'identity',
      label: 'combo.group.identity',
      items: [
        { field: 'code' },
        { field: 'name' },
        { field: 'description' },
        { field: 'status' },
        { field: 'price' },
      ],
    },
    {
      kind: 'fields',
      id: 'meta',
      label: 'combo.group.meta',
      // `id` sits with the other server-assigned stamps rather than in
      // `identity`: it is a read-only UUID, and `identity` is the edit form.
      items: [{ field: 'id' }, { field: 'created_at' }, { field: 'updated_at' }],
    },
    {
      kind: 'custom',
      id: 'slots',
      label: 'combo.slots.title',
      render: function ComboSlotsTab({ record }) {
        return <SlotEditorCard combo={record} />;
      },
    },
  ],
});

export const comboResource: ResourceBundle<Combo, typeof comboModel.fields> = {
  model: comboModel,
  list: comboListView,
  form: comboFormView,
  host: {
    api: {
      // The list page passes straight through (#1909); only the unwrapped
      // detail record still needs the legacy `ApiResponse` bridge (#1151).
      list: async (params) => toListResult(await comboAPI.list(params)),
      get: (id) => comboAPI.detail(id),
      create: (body) => comboAPI.create(body),
      update: (id, body) => comboAPI.update(id, body),
      delete: (id) => comboAPI.delete(id),
    },
    label: 'combo.label',
    slug: 'catalog-combos',
    listTitle: 'combo.listTitle',
    listDescription: 'combo.listDescription',
    // combos re-gated onto `catalog:read` — the backend folded the legacy
    // `combos:*` keys into the unified `catalog:*` module (#943).
    permission: {
      list: PERMISSIONS.CATALOG_READ,
      read: PERMISSIONS.CATALOG_READ,
      // Mutations require `catalog:write` (backend write routes enforce it);
      // gating the buttons here stops a read-only viewer seeing a control
      // the server would only 403 — the repo's forbidden deceptive-affordance.
      create: PERMISSIONS.CATALOG_WRITE,
      update: PERMISSIONS.CATALOG_WRITE,
      delete: PERMISSIONS.CATALOG_WRITE,
    },
    // `status` and NOTHING else (#2114). The reason is not `x-filterable` —
    // that seam advertises the kernel's `filter[field][op]=` DSL, which this
    // route does not parse, which is why the backend leaves it empty on
    // purpose. The reason is that `/api/catalog/combos` declares a bare
    // `status=` query parameter and `listCombos` applies it
    // (`contexts/kitchen/http/catalog-combos.ts`), and `comboAPI.list` already
    // whitelists exactly that. Omitting this key reads as "unrestricted":
    // measured through `ResourcePage` before this line existed, the field menu
    // offered all EIGHT columns, seven of which `toApiListParams` dropped
    // before the request, unfiltered and unwarned. This narrows the menu to
    // the one that works. Pinned to `eq` BY CONSTRUCTION, not by luck:
    // `serializeConditionsToParams` emits `eq` as a bare value, which is what
    // this parameter is; every other operator would arrive as `ne:archived` /
    // `in:a,b` and the route's zod enum would answer 400.
    filterable: { status: ['eq'] },
    // The same seam, one face over (#2114). `/api/catalog/combos` declares
    // `x-sortable: []` and its handler never reads a sort param, but an
    // ABSENT declaration made `createColumns` mark every header sortable.
    // Losing those headers is the point: each one issued a sort the route
    // ignores, which on screen is indistinguishable from a sort that ran.
    // Bound by reference so regeneration flows through (#1837 guardrail A).
    sortable: CATALOG_COMBO_SORTS,
    searchPlaceholder: 'combo.searchPlaceholder',
    formConfig: {
      createTitle: 'combo.form.createTitle',
      editTitle: 'combo.form.editTitle',
      columns: 2,
      maxWidth: 'md',
    },
  },
};
